import React, { ComponentProps } from "react";
import styled from "styled-components";
import { staticText } from "../../shared/statics/static-text";
import { primary03 } from "../../styles/tokens";
import { IconButton } from "../icon-button/IconButton.Parts";

// TEXT
export const text = staticText.copyable;

// STYLES
export const CopyableContainer = styled.div<{ $hoverable?: boolean }>`
  display: inline-flex;
  gap: 0.5rem;
  transition: color 0.3s ease;
  ${({ $hoverable }) =>
    $hoverable &&
    `
    cursor: pointer;
    &:hover {
      color: ${primary03};
    }
  `}
`;

export const CopyIconButton = styled(IconButton)<{ $disabled?: boolean }>`
  ${({ $disabled }) =>
    $disabled &&
    `
    pointer-events: none;
  `}
  color: inherit;
  height: unset;
  width: unset;
`;

export const TooltipText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// DATA

// TYPES
export type Props = ComponentProps<"div"> & {
  /**
   * Optional, the content to be displayed inside the Copyable component
   * If not provided, only the copy icon button will be rendered
   */
  children?: React.ReactNode;
  /**
   * Optional, if true and children is a string or number, the whole container will be clickable to copy the text.
   * If false, only the copy icon button will be clickable
   * @default true
   */
  autoHoverable?: boolean;
  /** Optional, if true, the tooltip will be disabled
   * @default false
   */
  disableTooltip?: boolean;
  /** Optional, custom tooltip text
   * @default "Click to copy"
   */
  tooltipText?: string;
  /** Optional, if true, no toast will be shown on copy success or failure
   * @default false
   */
  disableToast?: boolean;
  /** Optional, custom toast success message
   * @default "Copied to clipboard"
   */
  toastSuccessMessage?: string;
  /** Optional, custom toast error message
   * @default "Failed to copy to clipboard"
   */
  toastErrorMessage?: string;
  /**
   * Optional, if false it will only render the children without copy functionality
   * @default true
   */
  canCopy?: boolean;
  /**
   * Optional, the text to copy to the clipboard (if not provided, inner text from children will be used)
   */
  copyText?: string;
  /**
   * Optional, if true and children is a string, it will apply text ellipsis style
   * @default false
   */
  ellipsis?: boolean;
  /**
   * Optional, callback when copy action is performed
   */
  onCopy?: () => void;
  /**
   * Optional, callback when copy action failed
   */
  onCopyFailed?: () => void;
  /**
   * Optional, icon size (height and width)
   */
  iconSize?: string;
};

// CONSTANTS

// FUNCTIONS
const isElementWithChildren = (
  node: React.ReactNode,
): node is React.ReactElement<{ children?: React.ReactNode }> => {
  return React.isValidElement(node);
};
export const extractText = (node: React.ReactNode): string => {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number")
    return String(node).trim();
  if (Array.isArray(node)) return node.map(extractText).join("").trim();

  if (isElementWithChildren(node)) {
    return extractText(node.props.children).trim();
  }

  return "";
};

// ASSETS
